import { formatGel, formatPoints } from '../lib/format';
import type { CompletedOrder, Localized, OrderDetail } from '../lib/types';
import {
  invoiceDateTime, invoiceMethod, invoiceText, type InvoiceLanguage,
} from '../lib/invoice-text';

export interface InvoiceData {
  reference: string;
  completedAt: string;
  issuedBy: string | null;
  business: { name: string; phone: string; address: string; email: string };
  customer: { name: string; loyaltyId: string };
  vehicle: {
    make: string; model: string; year: number | null;
    color: string | null; licensePlate: string | null;
  } | null;
  lines: Array<{
    /**
     * Left as the server stored it so the receipt renders in whichever
     * language the person printing it reads. Flattening to one field here
     * would pin every receipt to the language of whoever wrote this code.
     */
    name: Localized | string;
    quantity: number;
    unitPriceTetri: number;
    lineTotalTetri: number;
    performedByName: string | null;
    performedByJobTitle: string | null;
  }>;
  payments: Array<{ method: string; amountTetri: number }>;
  notes: string | null;
  totals: {
    subtotalTetri: number; discountBp: number; discountTetri: number;
    cashbackUsedTetri: number; amountPayableTetri: number;
  };
  earned: { cashbackTetri: number; cashbackIsPending: boolean; points: number } | null;
  /** Only meaningful on a receipt printed at the counter; omitted on a reprint. */
  balancesAfter: { cashbackAvailableTetri: number; pointsBalance: number } | null;
}

/** The receipt as it is handed over right after payment. */
export function fromCompletedOrder(result: CompletedOrder): InvoiceData {
  return {
    reference: result.reference,
    completedAt: result.completedAt,
    issuedBy: result.issuedBy,
    business: result.business,
    customer: result.customer,
    vehicle: result.vehicle,
    lines: result.lines,
    payments: result.payments,
    notes: result.notes,
    totals: result.totals,
    earned: result.earned,
    balancesAfter: {
      cashbackAvailableTetri: result.balances.cashbackAvailableTetri,
      pointsBalance: result.balances.pointsBalance,
    },
  };
}

/**
 * The same receipt printed again later. Balances are left off deliberately:
 * they have moved on since, and printing today's numbers on an old order would
 * be a receipt that never existed.
 */
export function fromOrderDetail(detail: OrderDetail): InvoiceData {
  const { order } = detail;
  return {
    reference: order.reference,
    completedAt: order.completedAt ?? order.createdAt,
    issuedBy: order.completedByName ?? null,
    business: detail.business,
    customer: { name: order.customerName, loyaltyId: order.loyaltyId },
    vehicle: order.vehicleMake
      ? {
        make: order.vehicleMake,
        model: order.vehicleModel ?? '',
        year: order.vehicleYear,
        color: order.vehicleColor,
        licensePlate: order.vehiclePlate,
      }
      : null,
    lines: detail.items.map((item) => ({
      name: item.serviceNameSnapshot,
      quantity: item.quantity,
      unitPriceTetri: item.unitPriceTetri,
      lineTotalTetri: item.lineTotalTetri,
      performedByName: item.performedByName,
      performedByJobTitle: item.performedByJobTitle,
    })),
    payments: detail.payments.map((p) => ({ method: p.method, amountTetri: p.amountTetri })),
    notes: order.notes,
    totals: {
      subtotalTetri: order.subtotalTetri,
      discountBp: order.discountBp,
      discountTetri: order.discountTetri,
      cashbackUsedTetri: order.cashbackUsedTetri,
      amountPayableTetri: order.orderValueTetri - order.cashbackUsedTetri,
    },
    earned: {
      cashbackTetri: order.cashbackEarnedTetri,
      cashbackIsPending: false,
      points: order.pointsEarned,
    },
    balancesAfter: null,
  };
}

function Money({ tetri }: { tetri: number }) {
  return <span className="tabular">{formatGel(tetri)}</span>;
}

/**
 * The printable document.
 *
 * Laid out in plain black on white rather than the dark interface: a receipt
 * handed to a customer is paper, and printing the application's own palette
 * would empty a toner cartridge to produce something harder to read.
 */
export function Invoice({
  data, language = 'ru',
}: { data: InvoiceData; language?: InvoiceLanguage }) {
  const t = invoiceText(language);
  // The service name as the customer reads it, not as the portal is set.
  const named = (name: unknown): string => (
    typeof name === 'string'
      ? name
      : ((name as Record<string, string>)[language] || (name as Record<string, string>).en || '')
  );
  const { business, customer, vehicle, lines, totals, earned, payments } = data;
  // Cashback is already off the payable line, so counting it again would hide a debt.
  const paid = payments.reduce((sum, p) => sum + (p.method === 'CASHBACK' ? 0 : p.amountTetri), 0);
  const outstanding = Math.max(0, totals.amountPayableTetri - paid);

  const vehicleLine = vehicle
    ? [
      [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' '),
      vehicle.color,
      vehicle.licensePlate,
    ].filter(Boolean).join(' · ')
    : null;

  return (
    <article id="invoice" className="invoice-sheet">
      <header className="invoice-head flex flex-col gap-4 border-b-2 border-black/85 pb-4 sm:flex-row sm:items-start sm:justify-between sm:gap-8">
        <div className="flex items-center gap-3">
          <img src="/brand/logo.png" alt="" className="invoice-logo h-9 w-auto max-w-[7.5rem] shrink-0 object-contain" />
          <div>
            <div className="text-[0.95rem] font-bold tracking-tight">{business.name}</div>
            <div className="mt-0.5 text-[0.68rem] leading-snug text-black/60">
              {business.address && <div>{business.address}</div>}
              <div>
                {business.phone}
                {business.email && ` · ${business.email}`}
              </div>
            </div>
          </div>
        </div>

        <div className="invoice-meta shrink-0 sm:text-right">
          <div className="text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-black/50">
            {t('title')}
          </div>
          <div className="tabular mt-0.5 text-base font-bold">{data.reference}</div>
          <div className="tabular mt-1 text-[0.68rem] text-black/60">
            {invoiceDateTime(data.completedAt, language)}
          </div>
        </div>
      </header>

      <section className="invoice-parties grid gap-4 border-b border-black/15 py-4 sm:grid-cols-2 sm:gap-8">
        <div>
          <div className="text-[0.58rem] font-semibold uppercase tracking-[0.16em] text-black/45">
            {t('customer')}
          </div>
          <div className="mt-1 text-sm font-semibold">{customer.name}</div>
          <div className="tabular text-[0.68rem] text-black/55">{customer.loyaltyId}</div>
        </div>
        <div>
          <div className="text-[0.58rem] font-semibold uppercase tracking-[0.16em] text-black/45">
            {t('vehicle')}
          </div>
          <div className="mt-1 text-sm font-semibold">{vehicleLine ?? '—'}</div>
          {data.issuedBy && (
            <div className="text-[0.68rem] text-black/55">{t('servedBy', { name: data.issuedBy })}</div>
          )}
        </div>
      </section>

      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-black/25 text-[0.58rem] uppercase tracking-[0.14em] text-black/50">
            <th className="py-2 text-left font-semibold">{t('service')}</th>
            <th className="w-14 whitespace-nowrap py-2 text-right font-semibold sm:w-16">{t('qty')}</th>
            <th className="w-20 py-2 text-right font-semibold sm:w-28">{t('price')}</th>
            <th className="w-20 py-2 text-right font-semibold sm:w-28">{t('total')}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => (
            <tr key={index} className="border-b border-black/10 align-top">
              <td className="py-2.5 pr-3">
                <div className="font-medium">
                  {named(line.name)}
                </div>
                {line.performedByName && (
                  <div className="text-[0.68rem] text-black/55">
                    {line.performedByName}
                    {line.performedByJobTitle && ` · ${line.performedByJobTitle}`}
                  </div>
                )}
              </td>
              <td className="tabular py-2.5 text-right">{line.quantity}</td>
              <td className="py-2.5 text-right"><Money tetri={line.unitPriceTetri} /></td>
              <td className="py-2.5 text-right font-medium"><Money tetri={line.lineTotalTetri} /></td>
            </tr>
          ))}
        </tbody>
      </table>

      <section className="mt-4 flex justify-end">
        <dl className="w-full text-sm sm:w-72">
          <div className="flex justify-between py-1">
            <dt className="text-black/60">{t('subtotal')}</dt>
            <dd><Money tetri={totals.subtotalTetri} /></dd>
          </div>

          {totals.discountTetri > 0 && (
            <div className="flex justify-between py-1">
              <dt className="text-black/60">{t('discount', { percent: totals.discountBp / 100 })}</dt>
              <dd>−<Money tetri={totals.discountTetri} /></dd>
            </div>
          )}

          {totals.cashbackUsedTetri > 0 && (
            <div className="flex justify-between py-1">
              <dt className="text-black/60">{t('cashbackApplied')}</dt>
              <dd>−<Money tetri={totals.cashbackUsedTetri} /></dd>
            </div>
          )}

          <div className="mt-1 flex justify-between border-t-2 border-black/85 py-2 text-base font-bold">
            <dt>{t('toPay')}</dt>
            <dd><Money tetri={totals.amountPayableTetri} /></dd>
          </div>

          {payments.length > 0 && (
            <div className="flex justify-between py-1 text-[0.7rem] text-black/55">
              <dt>{t('paidBy')}</dt>
              <dd>
                {payments.map((p) => invoiceMethod(p.method, language)).join(', ')}
              </dd>
            </div>
          )}

          {/* A receipt for work that is not paid for yet has to say so, or the
              bold line above reads as a settled bill. */}
          {outstanding > 0 && (
            <>
              {paid > 0 && (
                <div className="flex justify-between py-1 text-[0.7rem] text-black/55">
                  <dt>{t('paid')}</dt>
                  <dd><Money tetri={paid} /></dd>
                </div>
              )}
              <div className="flex justify-between border-t border-black/15 py-1.5 text-sm font-bold">
                <dt>{t('outstanding')}</dt>
                <dd><Money tetri={outstanding} /></dd>
              </div>
            </>
          )}
        </dl>
      </section>

      <section className="mt-5 border-t border-black/15 pt-3 text-[0.7rem] text-black/60">
        <div className="flex flex-wrap justify-between gap-3">
          {earned && (
            <span>
              {t('cashbackEarned')} <strong className="tabular text-black">
                +{formatGel(earned.cashbackTetri)}
              </strong>
              {earned.cashbackIsPending && ` ${t('matures')}`}
              <span className="mx-2">·</span>
              {t('points')} <strong className="tabular text-black">+{formatPoints(earned.points)}</strong>
            </span>
          )}
          {data.balancesAfter && (
            <span>
              {t('balanceAfter')} <strong className="tabular text-black">
                {formatGel(data.balancesAfter.cashbackAvailableTetri)}
              </strong>
              {' · '}
              <strong className="tabular text-black">
                {formatPoints(data.balancesAfter.pointsBalance)} {t('pts')}
              </strong>
            </span>
          )}
        </div>

        {data.notes && (
          <p className="mt-2 whitespace-pre-wrap border-t border-black/10 pt-2">{data.notes}</p>
        )}

        <p className="mt-4 text-center text-[0.65rem] text-black/45">
          {t('thanks', { company: business.name })}
        </p>
      </section>
    </article>
  );
}
